import * as CurrencyFormatter from '@Components/Utilities/CurrencyFormatter';
import { getURLParameter, sandhillsRequest, sandhillsLogging } from '@Components/Utilities/SandhillsUtility';
import { SignalRConnectionStatus } from '@Components/Utilities/SandhillsConstants';

const transportMethods = ['webSockets', 'longPolling'];
// Isolated SignalR setup in the event we end up having to support multiple hubs
let _auctionTimeBiddingSignalRConnection = null;
let _auctionTimeBiddingSignalRHub = null;
let _isAuctionTimeMobileApp = false;
let _currentWatchedEventIds = [];

const setupAuctionTimeBiddingSignalRHub = (hubUrl, isEmployee, isMobileAppContext, eventIds, updateBiddingData, updateBiddingPausedStatus, userAuthID) => {
    _isAuctionTimeMobileApp = isMobileAppContext ?? false;
    _currentWatchedEventIds = eventIds;

    // Try catch around the connection creation in the event the SignalR library is not available
    // This will at least prevent the page from going completely blank
    try {
        _auctionTimeBiddingSignalRConnection = $.hubConnection();
    }
    catch (ex) { }
    if (_auctionTimeBiddingSignalRConnection == null || _auctionTimeBiddingSignalRConnection == undefined) {
        logAuctionMessage("SignalR connection failed: None available");
        return false;
    }

    _auctionTimeBiddingSignalRConnection.url = hubUrl;
    _auctionTimeBiddingSignalRHub = _auctionTimeBiddingSignalRConnection.createHubProxy("auctiontimebidding");
    if (_auctionTimeBiddingSignalRHub == null || _auctionTimeBiddingSignalRHub == undefined) {
        logAuctionMessage("SignalR connection failed: No hub was provided");
        return false;
    }

    _auctionTimeBiddingSignalRConnection.qs = { "referrerUrl": window.location.href, "userAuthID": userAuthID };
    _auctionTimeBiddingSignalRConnection.logging = isEmployee;

    _auctionTimeBiddingSignalRHub.on("jsUpdateMessage", function(Event_Pub, CurrentBid, NextBid, NumberOfBids, SecondsRemaining, OpeningBid, HighBidderTUID, sEndDate, SentTime, bidIncrement) {
        // Parse this into the object we'd expect back from the AuctionUpdate API for consistent data handling on the consuming side
        updateBiddingData([{
            EventIDPub: Event_Pub,
            EventID: parseInt(Event_Pub.split("-")[0]),
            NumberOfBids: NumberOfBids,
            CurrentBid: CurrentBid,
            NextBid: NextBid,
            SecondsRemaining: SecondsRemaining,
            OpeningBid: OpeningBid,
            HighBidderLowerPubTUID: HighBidderTUID,
            BidIncrement: bidIncrement,
        }], null);
    });

    _auctionTimeBiddingSignalRHub.on("jsBiddingPaused", function (biddingPaused) {
        updateBiddingPausedStatus(biddingPaused)
    });

    _auctionTimeBiddingSignalRConnection.disconnected(function () {
        logAuctionMessage("Connection disconnected, Reconnecting.", " Last Error ", _auctionTimeBiddingSignalRConnection?.lastError?.message);
        
        setTimeout(function () {
            startSignalRConnection(true);
        }, 3000); // attempt to restart connection after 3 seconds.
    });

    _auctionTimeBiddingSignalRConnection.reconnecting(function () {
        logAuctionMessage("Attempting to reconnect");
    });

    _auctionTimeBiddingSignalRConnection.reconnected(function () {
        logAuctionMessage("Attempt to reconnect has succeeded");
    });

    _auctionTimeBiddingSignalRConnection.connectionSlow(function () {
        // Alert user or log?
        logAuctionMessage("Slow connection detected");
    });

    startSignalRConnection(false);

    return true;
};

const changeWatchedAuctionGroups = (newEventIds) => {
    const connectionStatus = getSignalRConnectionStatus();
    if (connectionStatus == SignalRConnectionStatus.Connected && _auctionTimeBiddingSignalRHub) {
        // Change out the groups we're subscribed to
        _auctionTimeBiddingSignalRHub.invoke("removeFromGroups", _currentWatchedEventIds).done(function () {
            _currentWatchedEventIds = newEventIds;
            _auctionTimeBiddingSignalRHub.invoke("addToGroups", newEventIds, false);
        });
    }
    else {
        // Just update the list, we'll pick it up on the next connection
        _currentWatchedEventIds = newEventIds;
    }
}

const startSignalRConnection = (pushLatestData) => {
    _auctionTimeBiddingSignalRConnection.start({ jsonp: true, transport: transportMethods }).done(function () {
        _auctionTimeBiddingSignalRHub.invoke("addToGroups", _currentWatchedEventIds, pushLatestData);
        logAuctionMessage("Connection succeeded");
    }).fail(function (e) {
        console.log("SignalR connection failed: " + e);
    });
}

const extendAuctionTimeBidding = (routes, auctionBiddingData) => {
    if (auctionBiddingData?.TimeRemaining && auctionBiddingData.TimeRemaining <= 60) {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'extendAuction',
            isQuickBid: 'false'
        });

        const connectionStatus = getSignalRConnectionStatus();
        const nextBid = (auctionBiddingData?.CurrentBid || 0) + auctionBiddingData.Increment;

        if (!_isAuctionTimeMobileApp && connectionStatus == SignalRConnectionStatus.Connected && _auctionTimeBiddingSignalRHub && !getURLParameter(window.location.href, "ajaxBidding")) {
            // Connected to SignalR, use that to extend the auction
            _auctionTimeBiddingSignalRHub.invoke("extendAuction", auctionBiddingData.ListingID, auctionBiddingData.LegacySiteID, auctionBiddingData.LegacyBaseCatID, nextBid, auctionBiddingData.CurrentBid).fail(function (error) {
                console.log(error);
            }).done(function (data) {
                postExtendAuctionActions(routes, auctionBiddingData, data);
            });
        }
        else {
            // AJAX fallback, currently this is neccesary for the mobile app since WS do not support passing through the bearer
            sandhillsRequest({
                url: routes.ExtendAuction,
                data: {
                    ListingID: auctionBiddingData.ListingID,
                    EventID: auctionBiddingData.EventID,
                    SiteId: auctionBiddingData.LegacySiteID,
                    BaseCategoryId: auctionBiddingData.LegacyBaseCatID,
                    UserBid: nextBid,
                    CurrentBid: auctionBiddingData.CurrentBid,
                },
                method: 'post',
                withCredentials: true,
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                postExtendAuctionActions(routes, auctionBiddingData, data);
            })
            .catch((error) => {
                console.log(error);
            });
        }
    }
}

const placeAuctionTimeBid = (routes, auctionBiddingData, isQuickBid) => {
    return new Promise((resolve, reject) => {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'attemptedBid',
            isQuickBid: isQuickBid.toString(),
            isUsersFirstBid: auctionBiddingData.UserBidInfo?.UsersLastBid <= 0 ? 'true' : 'false'
        });

        const connectionStatus = getSignalRConnectionStatus();
        if (!_isAuctionTimeMobileApp && connectionStatus == SignalRConnectionStatus.Connected && _auctionTimeBiddingSignalRHub && !getURLParameter(window.location.href, "ajaxBidding")) {
            // Connected to SignalR, use that to place the bid
            _auctionTimeBiddingSignalRHub.invoke("placeBid", auctionBiddingData.ListingID, auctionBiddingData.LegacySiteID, auctionBiddingData.LegacyBaseCatID, auctionBiddingData.UserBid, auctionBiddingData.CurrencyCode, isQuickBid).fail(function (error) {
                postBidActions(routes, auctionBiddingData, isQuickBid, { Success: false, Message: error });
                reject(error);
            }).done(function (data) {
                data = postBidActions(routes, auctionBiddingData, isQuickBid, data);
                resolve(data);
            });
        }
        else {
            // AJAX fallback, currently this is neceesary for the mobile app since WS do not support passing through the bearer
            sandhillsRequest({
                url: routes.PlaceBid,
                data: {
                    ListingID: auctionBiddingData.ListingID,
                    SiteId: auctionBiddingData.LegacySiteID,
                    BaseCategoryId: auctionBiddingData.LegacyBaseCatID,
                    UserBid: auctionBiddingData.UserBid,
                    CurrencyCode: auctionBiddingData.CurrencyCode,
                    IsQuickBid: isQuickBid,
                },
                method: 'post',
                withCredentials: true,
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                data = postBidActions(routes, auctionBiddingData, isQuickBid, data);
                resolve(data);
            })
            .catch((error) => {
                postBidActions(routes, auctionBiddingData, isQuickBid, { Success: false, Message: error });
                reject(error);
            });
        }
    });
}

// This takes our existing AuctionTimeInfo : AuctionInfo object and patches in the updates from either SignalR or the AuctionUpdates API
const patchInUpdatedAuctionData = (currentAuctionBiddingData, auctionUpdateData, biddingInputPaused) => {
    if (auctionUpdateData.NumberOfBids < currentAuctionBiddingData.NumberOfBids) {
        return currentAuctionBiddingData; // Until we have a timestamp, say this is old data if the number of bids goes down
    }
    // copy the object just so we're not touching the original
    const updatedData = { ...currentAuctionBiddingData };

    // SignalR does not include the overarching bidding paused status. That comes from a separate push on that end
    // AuctionsUpdate API will return both the data and the overaching bidding paused status
    if (biddingInputPaused != null) {
        updatedData.IsAllBiddingPaused = biddingInputPaused;
    }

    if (auctionUpdateData) {
        // Patch in the updated data
        updatedData.NumberOfBids = auctionUpdateData.NumberOfBids;
        updatedData.CurrentBid = auctionUpdateData.CurrentBid;
        updatedData.CurrentBidFormatted = `${CurrencyFormatter.formatBidAmount(auctionUpdateData.CurrentBid, updatedData.CurrencyCode, updatedData.CultureCode)} ${updatedData?.Quantity > 1 || !updatedData.BidPriceLabel.includes("Head") ? updatedData.BidPriceLabel : ''}`;
        updatedData.NextBidAmount = CurrencyFormatter.formatBidAmount(auctionUpdateData.NextBid, updatedData.CurrencyCode, updatedData.CultureCode);
        updatedData.Increment = auctionUpdateData.BidIncrement;

        if (auctionUpdateData.SecondsRemaining >= 0) {
            updatedData.endTimeChanged = (auctionUpdateData.SecondsRemaining > updatedData.TimeLeft || updatedData.TimeLeft - auctionUpdateData.SecondsRemaining > -3 );
            updatedData.TimeLeft = auctionUpdateData.SecondsRemaining;
        }
        else {
            updatedData.TimeLeft = translator.translate('sandhills.tradepubwebsite.mvc.auctionhasended');
        }
        updatedData.TimeRemaining = auctionUpdateData.SecondsRemaining;
        // SignalR does not push the individual listing bidding paused status either
        if (auctionUpdateData.ListingIsPaused != null) {
            updatedData.IsListingPaused = auctionUpdateData.ListingIsPaused;
        }

        // See if we need to change any banners based on the new information
        if (updatedData.UserBidInfo && updatedData.UserBidInfo.UsersLastBid > 0 && auctionUpdateData.HighBidderLowerPubTUID) {
            if (updatedData.BiddingUserID == parseInt(auctionUpdateData.HighBidderLowerPubTUID)) {
                updatedData.UserBidInfo = { ...updatedData.UserBidInfo, UserIsOutBid: false, UserIsTieBidder: false, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.highbidder') };
            }
            else if (updatedData.UserBidInfo.UsersLastBid == auctionUpdateData.CurrentBid) {
                updatedData.UserBidInfo = { ...updatedData.UserBidInfo, UserIsOutBid: false, UserIsTieBidder: true, ListingBidStatusText: translator.translate('sandhills.tiebidder') };
            }
            else {
                updatedData.UserBidInfo = { ...updatedData.UserBidInfo, UserIsOutBid: true, UserIsTieBidder: false, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.outbidtext') };
            }
        }
    }

    return updatedData;
};

const getSignalRConnectionStatus = () => {
    // Surrounded this by a try catch in the event the Connection is unavailable and we can at least return Disconnected
    try {
        return _auctionTimeBiddingSignalRConnection.state;
    }
    catch (ex) { }

    return SignalRConnectionStatus.Disconnected;
}

const postBidActions = (routes, auctionBiddingData, isQuickBid, data) => {
    if (data && data.Success && data.UserBidInfo) {
        // Attach the proper translation since this does not come back from the endpoint
        if (data.IsTieBid) {
            data.UserBidInfo = { ...data.UserBidInfo, ListingBidStatusText: translator.translate('sandhills.tiebidder') };
        }
        else if (data.AutoOutbid) {
            data.UserBidInfo = { ...data.UserBidInfo, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.outbidtext') };
        }
        else {
            data.UserBidInfo = { ...data.UserBidInfo, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.highbidder') };
        }
    }

    if (!data || !data.Success) {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'failedBid',
            isQuickBid: isQuickBid.toString(),
            message: encodeURIComponent(data?.Message) || 'No response data',
        });
    }

    return data;
}

const postExtendAuctionActions = (routes, auctionBiddingData, data) => {
    if (!data || !data.Success) {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'failedAuctionExtension',
            isQuickBid: 'false',
            message: encodeURIComponent(data?.Message) || 'No response data',
        });
    }
}

const logAuctionMessage = (...args) => {
    // Base this message log on whether or not logging is enabled for SignalR in general
    if (_auctionTimeBiddingSignalRConnection?.logging == true) {
        console.log("[" + new Date().toTimeString() + "]", args);
    }
}

export {
    changeWatchedAuctionGroups,
    extendAuctionTimeBidding,
    getSignalRConnectionStatus,
    logAuctionMessage,
    placeAuctionTimeBid,
    patchInUpdatedAuctionData,
    setupAuctionTimeBiddingSignalRHub,
};